import { useEffect, useState } from "react";

import {
  Button,
  Callout,
  FormGroup,
  Icon,
  InputGroup,
  Intent,
} from "@blueprintjs/core";
import { addYears } from "date-fns";

import { Box, Flex } from "@/components/layout/flexbox";
import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { SUBTEXT_COLOR } from "@/css/constants";
import { ProgramStatus, useUpdateProgramMutation } from "@/graphql";
import { MainProgram, MiniProgram } from "@/types/programs";
import { areDifferentTimes, getBrowserTimezone } from "@/utils/datetime";

import { validateProgramInput } from "./AddProgramForm";
import { getMinDate } from "./utils";

interface UpdateProgramFormProps {
  onCompleted: () => void;
  onError: (error: Error) => void;
  program: MainProgram | MiniProgram;
}

export function UpdateProgramForm({
  onCompleted,
  onError,
  program,
}: UpdateProgramFormProps) {
  const [name, setName] = useState<string>();
  const [launchDate, setLaunchDate] = useState<string | null>();
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const maxDate = addYears(new Date(), 1);

  const isNewData = launchDate
    ? areDifferentTimes(launchDate, program.targetLaunchDate)
    : false;

  const minDate = getMinDate(
    [new Date(launchDate || ""), new Date()],
    isNewData,
  );

  useEffect(() => {
    if (program) {
      const { name, targetLaunchDate } = program;
      setName(name);
      setLaunchDate(targetLaunchDate);
    }
  }, [program]);

  const [updateProgram, { loading }] = useUpdateProgramMutation({
    onCompleted: (data) => {
      const { ok, msg } = data.updateProgram || {};
      if (ok) {
        onCompleted();
      } else {
        onError(Error(msg || "Something went wrong. Please try again."));
      }
    },
    onError,
  });

  const validation = validateProgramInput({
    name,
    launchDate,
    currentLaunchDate: program.targetLaunchDate,
  });

  const disableProgramName =
    program.status && [ProgramStatus.Complete].includes(program.status);

  const disableLaunchDate =
    program.status &&
    [ProgramStatus.Live, ProgramStatus.Complete].includes(program.status);

  const onUpdateProgram = () => {
    if (!validation.valid) {
      if (validation.message) {
        onError(Error(validation.message));
      }
    } else {
      updateProgram({
        variables: {
          name,
          targetLaunchDate: launchDate,
          programId: program.id,
          scheduleLaunchEmail: null,
        },
      });
    }
  };

  return (
    <Box width={1}>
      <Box fontWeight={600} fontSize={16}>
        Program Name
      </Box>
      <Box fontSize={14} color={SUBTEXT_COLOR} mb={2}>
        Eventually, this will be visible to participants
      </Box>
      <InputGroup
        onChange={(e) => {
          setName(e.target.value);
        }}
        value={name || ""}
        large={true}
        fill={true}
        placeholder="Program Name"
        autoFocus={true}
        disabled={loading || disableProgramName}
      />
      <Box fontWeight={600} fontSize={16} mt={3}>
        Target Launch Date{" "}
      </Box>
      <Box fontSize={14} color={SUBTEXT_COLOR} mb={2}>
        This is the date your participants will be invited to the program.
        <div>You can adjust this later</div>
      </Box>
      <Box css={{ position: "relative" }}>
        <Icon
          icon="calendar"
          style={{
            position: "absolute",
            top: 12,
            left: 10,
            zIndex: 99,
            color: SUBTEXT_COLOR,
          }}
        />
        <DateTimeInputV2
          onChange={setLaunchDate}
          value={launchDate}
          timezone={timezone}
          onChangeTimezone={setTimezone}
          maxDateTime={maxDate}
          minDateTime={minDate || undefined}
          disabled={loading || disableLaunchDate}
        />
      </Box>
      {isNewData && (
        <Callout
          icon={null}
          intent={Intent.WARNING}
          style={{ marginTop: 10, marginBottom: 10, fontSize: 14 }}
        >
          The launch date has been changed. This will:
          <ul className="list-disc pl-4">
            <li style={{ fontSize: 14 }}>
              Affect the timeline of all components that are launched with the
              program.
            </li>
            <li style={{ fontSize: 14 }}>
              Components which end after the new launch date will be skipped.
            </li>
          </ul>
        </Callout>
      )}
      {!validation.valid && validation?.errorType === "launchDate" && (
        <Box color={"#c23030"} fontSize={14} mt={10}>
          {validation?.message}
        </Box>
      )}
      <FormGroup style={{ margin: 0 }}>
        <Flex mt={3}>
          <Button
            style={{ minWidth: 160 }}
            large={true}
            intent="primary"
            disabled={loading || !validation.valid}
            onClick={onUpdateProgram}
          >
            {loading ? "Updating..." : "Save"}
          </Button>
        </Flex>
      </FormGroup>
    </Box>
  );
}
